/**
 * zzai-core.js
 * Shared utilities — loaded on every page via zzai-cart-drawer section.
 * Populated immediately (not in DOMContentLoaded) because defer scripts
 * run after all HTML is parsed, so #zzai-global-config is in the DOM.
 */

if (!window.ZZAI) {
  (function () {
    'use strict';

    /** Same-origin absolute URL for cart AJAX (avoids bad relative resolution). */
    function resolveStoreAjaxUrl(pathOrUrl) {
      const s = String(pathOrUrl || '').trim();
      if (!s) return s;
      try {
        if (/^https?:\/\//i.test(s)) return s;
        return new URL(s.startsWith('/') ? s : `/${s}`, window.location.origin).href;
      } catch {
        return s;
      }
    }

    /** Shopify Cart AJAX endpoints require a `.js` suffix. */
    function ensureJsSuffix(url) {
      const s = String(url || '').trim();
      if (!s) return s;
      const q = s.indexOf('?');
      const base = q === -1 ? s : s.slice(0, q);
      const query = q === -1 ? '' : s.slice(q);
      if (/\.js$/i.test(base)) return s;
      return `${base}.js${query}`;
    }

    function finiteDataset(el, key, fallback) {
      const v = parseFloat(el?.dataset?.[key]);
      return Number.isFinite(v) ? v : fallback;
    }

    function finiteDatasetCents(el, key, fallback = 0) {
      const v = parseInt(el?.dataset?.[key] ?? '', 10);
      return Number.isFinite(v) && v >= 0 ? v : fallback;
    }

    function dollarsToCents(dollars) {
      const n = Number(dollars);
      if (!Number.isFinite(n) || n < 0) return 0;
      return Math.round((n + Number.EPSILON) * 100);
    }

    function readCommerceJson() {
      const node = document.getElementById('zzai-commerce-config');
      if (!node) return null;
      try {
        const parsed = JSON.parse(String(node.textContent || '').trim());
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }

    function readConfig() {
      const el = document.getElementById('zzai-global-config');
      const commerce = readCommerceJson();
      const section = document.querySelector('.zzai-cart-section');
      const freeShippingCents = finiteDatasetCents(el, 'freeShippingCents', -1);
      const flatShippingCents = finiteDatasetCents(el, 'flatShippingCents', -1);
      const freeShippingDollars = finiteDataset(el, 'freeShipping', 0);
      const flatShippingDollars = finiteDataset(el, 'flatShipping', 0);
      const subDiscountLabel = String(
        commerce?.sub_pct_label ||
          el?.dataset?.subDiscountLabel ||
          section?.dataset?.subDiscountLabel ||
          '',
      ).trim();
      const commerceFsCents = parseInt(commerce?.fs_cents ?? '', 10);
      const commerceFlatCents = parseInt(commerce?.flat_cents ?? '', 10);
      const sectionFsCents = parseInt(section?.dataset?.freeShippingCents ?? '', 10);
      const sectionFlatCents = parseInt(section?.dataset?.flatShippingCents ?? '', 10);
      return {
        /** Mirrors theme “Subscription discount (%)”; cart/checkout use Shopify selling plan prices. */
        subDiscount: finiteDataset(el, 'subDiscount', commerce?.sub_pct ?? 0),
        subDiscountLabel,
        freeShipping: freeShippingDollars || parseFloat(commerce?.fs_dollars) || 0,
        flatShipping: flatShippingDollars || parseFloat(commerce?.flat_dollars) || 0,
        freeShippingCents:
          (Number.isFinite(commerceFsCents) && commerceFsCents > 0
            ? commerceFsCents
            : Number.isFinite(sectionFsCents) && sectionFsCents > 0
              ? sectionFsCents
              : freeShippingCents >= 0
                ? freeShippingCents
                : dollarsToCents(freeShippingDollars || commerce?.fs_dollars)),
        flatShippingCents:
          (Number.isFinite(commerceFlatCents) && commerceFlatCents > 0
            ? commerceFlatCents
            : Number.isFinite(sectionFlatCents) && sectionFlatCents > 0
              ? sectionFlatCents
              : flatShippingCents >= 0
                ? flatShippingCents
                : dollarsToCents(flatShippingDollars || commerce?.flat_dollars)),
        firstOrderDiscount: finiteDataset(el, 'firstOrderDiscount', commerce?.first_order_pct ?? 0),
        /** @deprecated use flatShipping */
        shipFlat: flatShippingDollars,
        tier1Pct: finiteDataset(el, 'tier1Pct', 0),
        tier2Pct: finiteDataset(el, 'tier2Pct', 0),
        tier3Pct: finiteDataset(el, 'tier3Pct', 0),
        showBundleDiscounts: el?.dataset.showBundleDiscounts !== 'false',
      };
    }

    function randomId(prefix) {
      const bytes = new Uint8Array(12);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(bytes);
        return `${prefix}_${Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('')}`;
      }
      return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    }

    function storageGet(key) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return '';
      }
    }

    function storageSet(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Ignore storage failures; analytics can still use in-memory ids.
      }
    }

    function sessionStorageGet(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return '';
      }
    }

    function sessionStorageSet(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        // Ignore storage failures.
      }
    }

    let memorySessionId = '';
    let memoryAnonymousId = '';

    function zzaiSessionId() {
      const key = 'zzai_session_id';
      let id = storageGet(key) || memorySessionId;
      if (!id) {
        id = randomId('zs');
        memorySessionId = id;
        storageSet(key, id);
      }
      return id;
    }

    function zzaiAnonymousId() {
      const key = 'zzai_anonymous_id';
      let id = storageGet(key) || memoryAnonymousId;
      if (!id) {
        id = randomId('za');
        memoryAnonymousId = id;
        storageSet(key, id);
      }
      return id;
    }

    function customerPrivacyConsent() {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      const consent = {};
      try {
        if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
          consent.analytics = !!privacy.analyticsProcessingAllowed();
        }
        if (privacy && typeof privacy.marketingAllowed === 'function') {
          consent.marketing = !!privacy.marketingAllowed();
        }
        if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
          consent.preferences = !!privacy.preferencesProcessingAllowed();
        }
        if (navigator.globalPrivacyControl) consent.globalPrivacyControl = true;
      } catch {
        // Consent APIs are optional and can differ by context.
      }
      return consent;
    }

    function analyticsAllowed() {
      if (navigator.globalPrivacyControl) return false;
      const consent = customerPrivacyConsent();
      return consent.analytics !== false;
    }

    function utmParams() {
      const out = {};
      try {
        const params = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'].forEach((key) => {
          const value = params.get(key);
          if (value) out[key] = value.slice(0, 300);
        });
      } catch {
        // No-op.
      }
      return out;
    }

    function nodeLabel(el) {
      if (!el) return '';
      return String(
        el.getAttribute('aria-label') ||
          el.getAttribute('data-analytics-label') ||
          el.textContent ||
          el.value ||
          '',
      ).trim().replace(/\s+/g, ' ').slice(0, 140);
    }

    function cleanSectionId(value) {
      return String(value || '')
        .replace(/^shopify-section-/, '')
        .replace(/^zzai-/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    function sectionTitle(section) {
      if (!section) return '';
      const explicit =
        section.getAttribute('data-analytics-section') ||
        section.getAttribute('data-section-title') ||
        section.getAttribute('aria-label');
      if (explicit) return String(explicit).trim().slice(0, 80);

      const heading = section.querySelector('h1, h2, h3, [data-analytics-heading]');
      const headingText = heading ? String(heading.textContent || '').trim().replace(/\s+/g, ' ') : '';
      if (headingText) return headingText.slice(0, 80);

      return cleanSectionId(section.id || section.getAttribute('data-section-id') || '').slice(0, 80);
    }

    function nearestSection(el) {
      const section = el && el.closest && el.closest('section[id], .shopify-section[id], [data-section-id], [id^="shopify-section"]');
      if (!section) return {};
      return {
        section: section.id || section.getAttribute('data-section-id') || '',
        sectionTitle: sectionTitle(section),
        sectionClass: String(section.className || '').slice(0, 180),
      };
    }

    function currentVisibleSection() {
      const candidates = Array.from(document.querySelectorAll('main section[id], main .shopify-section[id], main [data-section-id], [id^="shopify-section"]'));
      let best = null;
      let bestScore = 0;
      const vh = window.innerHeight || document.documentElement.clientHeight || 1;
      candidates.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
        const score = visible / Math.max(1, Math.min(rect.height || 1, vh));
        if (score > bestScore) {
          best = section;
          bestScore = score;
        }
      });
      return best && bestScore > 0.12 ? {
        section: best.id || best.getAttribute('data-section-id') || '',
        sectionTitle: sectionTitle(best),
        sectionClass: String(best.className || '').slice(0, 180),
      } : {};
    }

    /** ZZAI flavor slug from Shopify handle (data-sku → --sku-color in zzai.css). */
    function flavorSkuFromHandle(handle) {
      const h = String(handle || '').toLowerCase();
      if (h.includes('zobo')) return 'zobo';
      if (h.includes('obi')) return 'obi';
      if (h.includes('chapman')) return 'chapman';
      if (h.includes('tamtam')) return 'tamtam';
      if (h.includes('mixed') || h.includes('variety')) return 'variety';
      return '';
    }

    window.ZZAI = {
      dollarsToCents,
      flavorSkuFromHandle,

      /** Format cents as a dollar string, e.g. 4200 → "$42", 999 → "$9.99" */
      fmt(cents) {
        const n = Math.round(Number(cents) || 0);
        return '$' + (n / 100).toFixed(2).replace(/\.00$/, '');
      },

      /**
       * Format a percentage value cleanly: no trailing .0 for integers.
       * e.g. 15 → "15",  7.5 → "7.5",  7.0 → "7"
       */
      fmtPct(val) {
        const n = parseFloat(val) || 0;
        return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
      },

      /** JSON POST helper — mirrors the old local jsonPost() in cart/bundle/pledge */
      jsonPost(url, body) {
        return fetch(url, {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:        JSON.stringify(body),
        });
      },

      analyticsAllowed,

      sessionId() {
        return zzaiSessionId();
      },

      anonymousId() {
        return zzaiAnonymousId();
      },

      attachAnalyticsSessionToCart() {
        if (!analyticsAllowed()) return Promise.resolve(false);
        const key = `zzai_cart_session_attached:${zzaiSessionId()}`;
        if (sessionStorageGet(key) === '1') return Promise.resolve(true);
        sessionStorageSet(key, '1');
        return this.jsonPost(this.cartUpdateJsUrl(), {
          attributes: {
            zzai_session_id: zzaiSessionId(),
            zzai_landing_page: window.location.pathname,
            zzai_referrer: document.referrer || '',
          },
        }).then(() => true).catch(() => false);
      },

      track(eventName, properties) {
        const event = String(eventName || '').trim();
        if (!event || !analyticsAllowed()) return Promise.resolve(false);
        const eventProps = Object.assign({}, properties || {});
        const experiments = window.ZZAI_EXPERIMENTS || {};
        if (experiments.hero && !eventProps.experiment_hero) eventProps.experiment_hero = experiments.hero;
        if (!eventProps.section && !eventProps.sectionId && !eventProps.sectionTitle && !eventProps.sectionClass) {
          Object.assign(eventProps, currentVisibleSection());
        }
        const payload = {
          event,
          properties: eventProps,
          sessionId: zzaiSessionId(),
          anonymousId: zzaiAnonymousId(),
          pagePath: window.location.pathname + window.location.search,
          pageTitle: document.title || '',
          referrer: document.referrer || '',
          utm: utmParams(),
          consent: customerPrivacyConsent(),
          ts: Date.now(),
        };

        const body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
          try {
            const ok = navigator.sendBeacon('/apps/zzai/analytics/event', new Blob([body], { type: 'application/json' }));
            if (ok) return Promise.resolve(true);
          } catch {
            // Fall back to fetch.
          }
        }

        return fetch('/apps/zzai/analytics/event', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: body.length < 60000,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        }).then((response) => response.ok).catch(() => false);
      },

      /** `Theme.routes` from `snippets/scripts.liquid` (locale-aware). */
      themeRoutes() {
        return (typeof window !== 'undefined' && window.Theme && window.Theme.routes) || null;
      },
      cartAddJsUrl() {
        const r = this.themeRoutes();
        return resolveStoreAjaxUrl(ensureJsSuffix((r && r.cart_add_url) || '/cart/add'));
      },
      cartJsUrl() {
        const r = this.themeRoutes();
        return resolveStoreAjaxUrl(ensureJsSuffix((r && r.cart_js_url) || '/cart'));
      },
      cartChangeJsUrl() {
        const r = this.themeRoutes();
        return resolveStoreAjaxUrl(ensureJsSuffix((r && r.cart_change_url) || '/cart/change'));
      },
      cartUpdateJsUrl() {
        const r = this.themeRoutes();
        return resolveStoreAjaxUrl(ensureJsSuffix((r && r.cart_update_url) || '/cart/update'));
      },
      checkoutUrl() {
        const r = this.themeRoutes();
        return resolveStoreAjaxUrl((r && r.checkout_url) || '/checkout');
      },

      isLocalThemePreview() {
        const h = window.location && window.location.hostname;
        return h === '127.0.0.1' || h === 'localhost';
      },

      /** User-facing message when cart fetch/jsonPost fails (incl. theme dev proxy 502/403). */
      cartFetchErrorMessage(err) {
        const status = err && err.status;
        const msg = err && err.message ? String(err.message) : '';
        const localPreview = this.isLocalThemePreview();
        const localHint = localPreview
          ? ' Cart APIs (/cart/change.js, /cart/add.js) often fail on 127.0.0.1 — use the myshopify.com preview link from `shopify theme dev` instead of localhost.'
          : '';

        if (
          status === 502 ||
          status === 503 ||
          status === 504 ||
          status === 403 ||
          (err && err.isBotChallenge) ||
          (err && err.isCartHtmlResponse)
        ) {
          if (status === 403) {
            return (
              'Shopify returned 403 Forbidden (security / bot check / preview proxy).' +
              localHint +
              ' Stop theme dev, clear cookies for 127.0.0.1, restart `shopify theme dev`, or use the myshopify.com preview URL.'
            );
          }
          return (
            'Cart is unavailable in local theme preview (proxy or security check).' + localHint
          );
        }
        if (
          !msg ||
          msg === 'fetch failed' ||
          /failed to fetch/i.test(msg) ||
          /fetch failed/i.test(msg) ||
          err instanceof TypeError
        ) {
          return 'Could not reach the cart API.' + localHint;
        }
        return msg + (localPreview && !/127\.0\.0\.1|localhost|theme preview/i.test(msg) ? localHint : '');
      },
      /** Normalized cart page path for matching header links, e.g. `/cart` or `/en/cart`. */
      cartPagePath() {
        const r = this.themeRoutes();
        const raw = (r && r.cart_url) || '/cart';
        try {
          return new URL(raw, window.location.origin).pathname.replace(/\/$/, '') || '/cart';
        } catch {
          return '/cart';
        }
      },

      /**
       * Sanitize a CSS color value coming from untrusted data (e.g. line item
       * properties).  Only allows known-safe patterns; anything else gets the
       * fallback.  Prevents CSS injection via custom properties.
       */
      safeCSSColor(val, fallback) {
        fallback = fallback || 'var(--color-gold)';
        if (!val) return fallback;
        const s = String(val).trim();
        return /^(#[0-9a-fA-F]{3,8}|rgb[a]?\(|hsl[a]?\(|var\(--)/.test(s) ? s : fallback;
      },

      /** Returns a debounced version of fn that fires ms after the last call. */
      debounce(fn, ms) {
        let timer;
        return function (...args) {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), ms);
        };
      },

      /** Cached config — call getConfig() everywhere; reads DOM only once. */
      _cfg: null,
      getConfig() {
        if (!this._cfg) this._cfg = readConfig();
        return this._cfg;
      },

      isGenericSellingPlanName(name) {
        const s = String(name || '').trim();
        if (!s) return true;
        if (/^subscription\.?$/i.test(s)) return true;
        return /^subscribe\s*&\s*save/i.test(s) && !/\d/.test(s) && !/\b(week|month|day)s?\b/i.test(s);
      },

      normalizePlanOptions(options) {
        if (!Array.isArray(options)) return [];
        return options
          .map((o) => {
            if (o == null) return null;
            if (typeof o === 'string') {
              const v = o.trim();
              return v ? { name: '', value: v } : null;
            }
            const value = String(o.value ?? o).trim();
            const name = String(o.name || '').trim();
            return value || name ? { name, value: value || name } : null;
          })
          .filter(Boolean);
      },

      cadenceLabelFromIntervalValue(val) {
        const s = String(val || '').trim();
        if (!s) return '';
        if (/^every\b/i.test(s)) return s;
        let m = s.match(/(\d+)\s*(week|month|day)s?\b/i);
        if (m) {
          const n = parseInt(m[1], 10);
          const u = m[2].toLowerCase();
          if (n === 1) return `Every ${u}`;
          return `Every ${n} ${u}s`;
        }
        return `Every ${s}`;
      },

      humanizeSellingPlanLabel(name) {
        const s = String(name || '').trim();
        if (!s) return '';
        if (/^delivered\s+every/i.test(s)) return s;
        let m = s.match(/^delivery\s+every\s+(\d+)\s*(week|month|day)s?\b/i);
        if (m) return `Delivered every ${m[1]} ${m[2].toLowerCase()}${Number(m[1]) === 1 ? '' : 's'}`;
        m = s.match(/\bevery\s+(\d+)\s*(week|month|day)s?\b/i);
        if (m) return `Delivered every ${m[1]} ${m[2].toLowerCase()}${Number(m[1]) === 1 ? '' : 's'}`;
        return s;
      },

      /** Label from Shopify selling_plan.options (cadence) then name. */
      sellingPlanDisplayLabel(planOrAlloc) {
        const plan = planOrAlloc?.selling_plan || planOrAlloc || {};
        const rawName = String(plan.name || planOrAlloc?.name || '').trim();
        const options = this.normalizePlanOptions(plan.options || planOrAlloc?.options);

        let deliveryVal = '';
        for (const opt of options) {
          const val = String(opt.value || '').trim();
          if (!val) continue;
          const key = String(opt.name || '').toLowerCase();
          if (/delivery|frequency|every|ship|interval|billing/.test(key)) {
            deliveryVal = val;
            break;
          }
        }
        if (!deliveryVal) {
          const first = options.map((o) => String(o.value || '').trim()).find(Boolean);
          if (first) deliveryVal = first;
        }

        if (deliveryVal) return this.cadenceLabelFromIntervalValue(deliveryVal);

        const desc = String(plan.description || planOrAlloc?.description || '').trim();
        if (desc && /\d+\s*(week|month|day)/i.test(desc)) {
          return this.humanizeSellingPlanLabel(desc);
        }

        if (!this.isGenericSellingPlanName(rawName)) {
          return this.humanizeSellingPlanLabel(rawName);
        }

        return '';
      },

      /** Plans from product.js — selling_plan_groups (labels) + variant allocations (prices). */
      collectSellingPlansFromProduct(product) {
        const seen = new Set();
        const out = [];
        const push = (id, name, options, sp) => {
          const idStr = String(id ?? '').trim();
          if (!idStr || seen.has(idStr)) return;
          seen.add(idStr);
          const normalized = this.normalizePlanOptions(options);
          const selling_plan = sp || { id, name, options: normalized };
          out.push({
            id,
            name: name || '',
            options: normalized,
            selling_plan,
          });
        };

        for (const g of product?.selling_plan_groups || []) {
          for (const sp of g.selling_plans || []) {
            push(sp.id, sp.name, sp.options, sp);
          }
        }

        for (const v of product?.variants || []) {
          for (const a of v.selling_plan_allocations || []) {
            const sp = a.selling_plan || {};
            push(a.selling_plan_id ?? sp.id, sp.name || a.name, sp.options, sp);
          }
        }

        return out;
      },

      sellingPlanSortKey(plan) {
        const label = String(plan?.displayName || plan?.name || '');
        const m = label.match(/(\d+)\s*(week|month|day)/i);
        if (!m) return 999;
        const n = parseInt(m[1], 10);
        const u = m[2].toLowerCase();
        const unit = u.startsWith('day') ? 1 : u.startsWith('week') ? 7 : 30;
        return n * unit;
      },

      normalizeSellingPlans(raw) {
        const Z = this;
        const plans = (Array.isArray(raw?.plans) ? raw.plans : [])
          .map(p => {
            const id = p?.id;
            const options = Array.isArray(p?.options) ? p.options : [];
            const displayName =
              String(p?.displayName || '').trim() ||
              Z.sellingPlanDisplayLabel({ name: p?.name, options }) ||
              String(p?.name || '').trim() ||
              'Subscription';
            return { id, name: p?.name || '', displayName, options };
          })
          .filter(p => p.id != null && String(p.id) !== '');

        plans.sort((a, b) => Z.sellingPlanSortKey(a) - Z.sellingPlanSortKey(b));

        let defaultId = raw?.default != null ? String(raw.default) : '';
        if (!defaultId) {
          const four = plans.find(p => /4\s*week/i.test(p.displayName));
          defaultId = four ? String(four.id) : plans[0] ? String(plans[0].id) : '';
        }

        return { plans, default: defaultId };
      },

      /** Fill [data-zzai-sub-pct] and .zzai-sub-pct from commerce JSON / global config. */
      hydrateSubscriptionPctLabels() {
        const commerce = readCommerceJson();
        const cfg = readConfig();
        const label = String(
          commerce?.sub_pct_label ||
            cfg.subDiscountLabel ||
            this.fmtPct(commerce?.sub_pct ?? cfg.subDiscount) ||
            '',
        ).trim();
        if (!label || label === '0') return;
        document.querySelectorAll('[data-zzai-sub-pct], .zzai-sub-pct').forEach((el) => {
          el.textContent = label;
        });
      },
    };

    // Eagerly populate config while DOM is guaranteed to be parsed
    window.ZZAI.getConfig();
    window.ZZAI.hydrateSubscriptionPctLabels();
    window.ZZAI.attachAnalyticsSessionToCart();
    window.ZZAI.track('page_viewed', {
      path: window.location.pathname,
      template: document.body && document.body.getAttribute('data-template'),
    });

    document.addEventListener('zzai:track', function (event) {
      const detail = event && event.detail || {};
      window.ZZAI.track(detail.event || detail.name, detail.properties || {});
    });

    if ('IntersectionObserver' in window) {
      const seen = new Set();
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.45) return;
          const el = entry.target;
          const key = el.id || el.getAttribute('data-section-id') || el.className;
          if (!key || seen.has(key)) return;
          seen.add(key);
          window.ZZAI.track('section_viewed', {
            section: el.id || '',
            sectionTitle: sectionTitle(el),
            sectionClass: String(el.className || '').slice(0, 180),
          });
          observer.unobserve(el);
        });
      }, { threshold: [0.45, 0.7] });

      document.querySelectorAll('main section[id], .shopify-section[id], [id^="shopify-section"]').forEach(function (el) {
        observer.observe(el);
      });
    }

    document.addEventListener('click', function (event) {
      const target = event.target && event.target.closest && event.target.closest('a, button, [role="button"], input[type="submit"]');
      if (!target) return;
      const label = nodeLabel(target);
      const href = target.href || target.getAttribute('href') || '';
      const action = target.getAttribute('data-action') || '';
      const lower = `${label} ${href} ${action}`.toLowerCase();
      const isAddToCartControl =
        target.matches(
          '[data-add-to-cart], [data-hero-quick-add], [data-sp-variety-add], [data-add-result], ' +
          '.fc-add, .sp-add, .add-to-cart-button, #bundle-add, #bundle-mobile-add'
        ) ||
        /add(?:\s+\w+){0,3}\s+to\s+(?:your\s+)?cart|cart\/add/.test(lower);
      let name = 'cta_clicked';
      if (/review/.test(lower)) name = 'review_cta_clicked';
      // A CLICK on add-to-cart is intent, not a confirmed add — the accurate
      // cart_add fires below on the theme's cart:update success event. The gap
      // between the two is a real friction signal (clicked but nothing landed).
      else if (isAddToCartControl) name = 'add_to_cart_click';
      else if (/subscribe|subscription|selling[_-]?plan/.test(lower)) name = 'subscription_intent';
      else if (/checkout/.test(lower)) name = 'checkout_started';
      else if (/quiz/.test(lower)) name = 'quiz_interaction';

      window.ZZAI.track(name, {
        label,
        href,
        action,
        tag: target.tagName,
        ...nearestSection(target),
      });
    }, true);

    // Accurate add-to-cart: the theme dispatches cart:update (CartAddEvent)
    // only after the /cart/add call succeeds, carrying the added variant. This
    // is the trustworthy funnel signal — no click guessing, no failed adds.
    document.addEventListener('cart:update', function (event) {
      try {
        const data = (event && event.detail && event.detail.data) || {};
        // Custom quick-add surfaces can confirm success with the refreshed cart
        // count even when Shopify's event payload doesn't include a variant.
        if (data.didError || (!data.variantId && !(Number(data.itemCount) > 0))) return;
        window.ZZAI.track('cart_add', {
          variantId: String(data.variantId || ''),
          productId: String(data.productId || ''),
          itemCount: Number(data.itemCount) || null,
          source: data.source || 'cart_event',
        });
      } catch (err) {
        // Never let analytics break the cart.
      }
    });

    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!form || !form.querySelector) return;
      const action = form.getAttribute('action') || '';
      const method = form.getAttribute('method') || '';
      const tags = form.querySelector('[name="contact[tags]"]')?.value || '';
      const subject = form.querySelector('[name="contact[Subject]"], [name="contact[subject]"]')?.value || '';
      const section = nearestSection(form);
      const haystack = `${action} ${tags} ${subject} ${section.section} ${section.sectionClass}`.toLowerCase();
      let leadType = 'contact';
      if (/newsletter|customer/.test(haystack)) leadType = 'newsletter';
      else if (/wholesale/.test(haystack)) leadType = 'wholesale';
      else if (/review/.test(haystack)) leadType = 'review';
      else if (/quiz/.test(haystack)) leadType = 'quiz';
      window.ZZAI.track('lead_submitted', {
        leadType,
        action,
        method,
        tags,
        subject,
        ...section,
      });
    }, true);

    function renderPdpReviews(root, reviews) {
      const list = root.querySelector('[data-pdp-live-review-list]');
      const block = root.closest('#pdp-reviews') || root.parentElement;
      const empty = block && block.querySelector('[data-pdp-review-empty]');
      const heading = block && block.querySelector('.pdp-reviews-head h2');
      const productHandle = root.getAttribute('data-product-handle') || '';
      const productName = root.getAttribute('data-product-name') || productHandle || 'ZZAI';
      const emptyReviewLink = empty && empty.querySelector('.pdp-reviews-foot a[href]');
      const reviewsHubUrl = emptyReviewLink ? emptyReviewLink.getAttribute('href') : '/pages/reviews';
      const writeReviewUrl = `${reviewsHubUrl.split('#')[0]}?source=pdp&product=${encodeURIComponent(productHandle)}&product_title=${encodeURIComponent(productName)}#write-review`;
      if (!list || !Array.isArray(reviews) || !reviews.length) return;

      function escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      function stars(rating) {
        const count = Math.max(1, Math.min(5, Number(rating) || 5));
        return Array(count + 1).join('★');
      }

      list.innerHTML = reviews.map((review) => ([
        '<article class="pdp-live-review-card">',
          `<div class="stars" aria-label="${escapeHtml(review.rating)} stars">${stars(review.rating)}</div>`,
          review.title ? `<strong>"${escapeHtml(review.title)}"</strong>` : '',
          `<p>${escapeHtml(review.body)}</p>`,
          '<footer>',
            `<span>${escapeHtml(review.authorName || 'ZZAI customer')}</span>`,
            '<span class="badge">Verified purchase</span>',
          '</footer>',
        '</article>',
      ].join(''))).join('');
      list.hidden = false;
      list.removeAttribute('hidden');
      let actions = root.querySelector('[data-pdp-live-review-actions]');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'pdp-live-review-actions';
        actions.setAttribute('data-pdp-live-review-actions', '');
        root.appendChild(actions);
      }
      actions.innerHTML = [
        `<a class="btn gold" href="${escapeHtml(writeReviewUrl)}">Write a review →</a>`,
        `<a class="btn ghost" href="${escapeHtml(reviewsHubUrl)}">Visit reviews hub →</a>`,
      ].join('');
      if (empty) {
        empty.hidden = true;
        empty.setAttribute('aria-hidden', 'true');
        empty.style.display = 'none';
      }
      if (heading) {
        heading.innerHTML = reviews.length === 1
          ? 'What sippers <em>say.</em>'
          : 'What sippers <em>say.</em>';
      }
      document.querySelectorAll('.pdp-be-first').forEach((link) => {
        const averageRating = reviews.reduce((total, review) => total + (Number(review.rating) || 0), 0) / reviews.length;
        const roundedRating = Math.max(1, Math.min(5, Math.round(averageRating)));
        const starLabel = `${averageRating.toFixed(1)} out of 5 stars`;
        link.innerHTML = [
          `<span class="pdp-review-stars" aria-hidden="true">${'★'.repeat(roundedRating)}${'☆'.repeat(5 - roundedRating)}</span>`,
          `<span>${reviews.length} review${reviews.length === 1 ? '' : 's'} →</span>`,
        ].join('');
        link.setAttribute('href', '#pdp-reviews');
        link.setAttribute('aria-label', `${starLabel}. Jump to ${reviews.length} review${reviews.length === 1 ? '' : 's'}`);
      });
    }

    document.querySelectorAll('[data-zzai-pdp-reviews]').forEach((root) => {
      const handle = root.getAttribute('data-product-handle') || '';
      if (!handle) return;
      fetch(`/apps/zzai/reviews?product=${encodeURIComponent(handle)}&limit=12`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (data && data.ok) renderPdpReviews(root, data.reviews || []);
        })
        .catch(() => {});
    });
  })();
}
